import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { RotateCcw, Search } from "lucide-react";
import ProductCard from "./ProductCard";
import { fetchProducts } from "../lib/api";

export default function Shop({ categories, active, setActive }) {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [sort, setSort] = useState("featured");
  const [products, setProducts] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    let live = true;
    setProducts(null);
    setError(null);
    fetchProducts({ category: active, q: debounced })
      .then((d) => live && setProducts(d))
      .catch((e) => {
        if (!live) return;
        setError(e.message);
        setProducts([]);
      });
    return () => {
      live = false;
    };
  }, [active, debounced]);

  const sorted = useMemo(() => {
    if (!products) return null;
    const arr = [...products];
    if (sort === "price-asc") arr.sort((a, b) => a.price - b.price);
    if (sort === "price-desc") arr.sort((a, b) => b.price - a.price);
    return arr;
  }, [products, sort]);

  return (
    <section id="shop" className="relative py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="flex flex-wrap items-end justify-between gap-6"
        >
          <div>
            <p className="font-mono text-[11px] tracking-[0.34em] uppercase text-ember">The full collection</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-starlight">
              Pick your fire
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <label className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-night" />
              <input
                data-testid="product-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search crackers…"
                className="w-52 sm:w-64 rounded-full border border-starlight/15 bg-card py-2.5 pl-10 pr-4 text-sm text-starlight placeholder:text-muted-night/60 outline-none transition-colors focus:border-ember/70"
              />
            </label>
            <select
              data-testid="sort-select"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="rounded-full border border-starlight/15 bg-card py-2.5 pl-4 pr-8 text-sm text-starlight outline-none focus:border-ember/70 appearance-none cursor-pointer"
            >
              <option value="featured">Featured</option>
              <option value="price-asc">Price: Low to High</option>
              <option value="price-desc">Price: High to Low</option>
            </select>
          </div>
        </motion.div>

        <div className="mt-8 flex flex-wrap items-center gap-2">
          <button
            data-testid="category-filter-all"
            onClick={() => setActive(null)}
            className={`rounded-full px-4 py-2 font-mono text-[11px] tracking-[0.16em] uppercase transition-all duration-300 border ${
              active === null
                ? "bg-ember text-obsidian border-ember font-bold"
                : "border-starlight/15 text-muted-night hover:border-ember/60 hover:text-ember"
            }`}
          >
            All
          </button>
          {categories.map((c) => (
            <button
              key={c.slug}
              data-testid={`category-filter-${c.slug}`}
              onClick={() => setActive(c.slug === active ? null : c.slug)}
              className={`rounded-full px-4 py-2 font-mono text-[11px] tracking-[0.16em] uppercase transition-all duration-300 border ${
                active === c.slug
                  ? "bg-ember text-obsidian border-ember font-bold"
                  : "border-starlight/15 text-muted-night hover:border-ember/60 hover:text-ember"
              }`}
            >
              {c.label}
            </button>
          ))}

        </div>

        <p className="mt-6 font-mono text-xs text-muted-night" data-testid="product-count">
          {sorted ? `${sorted.length} cracker${sorted.length === 1 ? "" : "s"} found` : "Loading the shelf…"}
        </p>

        {error && (
          <div className="mt-8 rounded-2xl border border-crimson/40 bg-crimson/10 p-6 text-sm text-starlight" data-testid="shop-error">
            {error} — please refresh.
          </div>
        )}

        {sorted && sorted.length === 0 && !error && (
          <div className="mt-10 flex flex-col items-center gap-4 rounded-2xl border border-dashed border-starlight/15 py-16 text-center" data-testid="shop-empty-state">
            <p className="font-display text-2xl text-starlight">Nothing matched that spark</p>
            <p className="max-w-sm text-sm text-muted-night">
              Try a different name — or clear the filters to see all 50+ crackers.
            </p>
            <button
              data-testid="reset-filters-button"
              onClick={() => {
                setQ("");
                setActive(null);
              }}
              className="mt-2 inline-flex items-center gap-2 rounded-full border border-ember/50 px-6 py-2.5 font-mono text-[11px] tracking-[0.2em] uppercase text-ember hover:bg-ember hover:text-obsidian transition-all"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Reset filters
            </button>
          </div>
        )}

        <motion.div
          initial="hidden"
          animate="show"
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.05 } } }}
          className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 sm:gap-6"
        >
          {sorted?.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </motion.div>
      </div>
    </section>
  );
}
